import { round } from 'lodash';
import { useMemo } from 'react';
import { Filter } from 'components/molecules/filters/types';
import {
  DateRange,
  getPreviousRange,
} from 'components/organisms/projects-report/common/range-picker/types';
import {
  ProjectsReport,
  ProjectsReportRecord,
} from 'components/organisms/projects-report/types';
import {
  WidgetState,
  WidgetStateError,
} from 'components/organisms/projects-report/widgets';
import { useProjectsReport } from 'components/pages/projects-report/use-projects-report';
import { Loaded, Loading } from 'core/state';
import { getProjectRecordsByTemplate } from '../../utils';

interface AbstractTemplateInsightWidgetData<T> {
  noOfProjects: T;
  avgDuration: T;
  percentageStartedOnTime: T;
  averageStartingDelay: T;
  percentageCompletedOnTime: T;
  averageCompletionDelay: T;
}
export type TemplateInsightWidgetCompareType = {
  value: number;
  isGood: boolean;
  hasIncreased: number;
};

type BaseWidgetData =
  | Loading
  | Loaded<AbstractTemplateInsightWidgetData<number>>;

type CompareInfo =
  AbstractTemplateInsightWidgetData<TemplateInsightWidgetCompareType>;
type NumberWidgetInfo = AbstractTemplateInsightWidgetData<
  WidgetState<number, WidgetStateError>
>;
type WidgetInfo = {
  currentWidgetInfo: NumberWidgetInfo;
  compareInfo?: CompareInfo;
};
const convertToWidgetData = <
  T extends number | TemplateInsightWidgetCompareType,
>(
  obj: Record<keyof AbstractTemplateInsightWidgetData<T>, T>,
): AbstractTemplateInsightWidgetData<WidgetState<T, WidgetStateError>> => {
  return Object.entries(obj).reduce(
    (data, [key, value]) => ({
      ...data,
      [key]:
        value == null || Number.isNaN(value)
          ? WidgetState.noData()
          : WidgetState.data(value),
    }),
    {} as AbstractTemplateInsightWidgetData<WidgetState<T, WidgetStateError>>,
  );
};

const totalOn =
  (key: keyof ProjectsReportRecord) =>
  (acc: number, record: ProjectsReportRecord) =>
    ((record?.[key] as number) ?? 0) + acc;
const isStartedOnTime = (record: ProjectsReportRecord) =>
  (record?.startingDelay ?? Infinity) <= 0;
const isCompletedOnTime = (record: ProjectsReportRecord) =>
  (record?.durationDifference ?? Infinity) <= 0;

const getProjectWidgetInTemplateInsight = (
  projectsReport: ProjectsReport,
): BaseWidgetData => {
  const [loadingState] = projectsReport;

  if (loadingState === 'loading') {
    return Loading();
  }

  const records = getProjectRecordsByTemplate(projectsReport);

  const noOfProjects = records.length;

  const avgDuration = round(
    records.reduce(totalOn('durationActual'), 0) / noOfProjects,
    2,
  );

  const percentageStartedOnTime = round(
    (records.filter(isStartedOnTime).length / noOfProjects) * 100,
    2,
  );
  const averageStartingDelay = round(
    records.reduce(totalOn('startingDelay'), 0) / noOfProjects,
    2,
  );
  const percentageCompletedOnTime = round(
    (records.filter(isCompletedOnTime).length / noOfProjects) * 100,
    2,
  );
  const averageCompletionDelay = round(
    records.reduce(totalOn('durationDifference'), 0) / noOfProjects,
    2,
  );

  return Loaded({
    noOfProjects,
    avgDuration,
    percentageStartedOnTime,
    averageStartingDelay,
    percentageCompletedOnTime,
    averageCompletionDelay,
  });
};

const getCompareInfo = (
  currentWidget: AbstractTemplateInsightWidgetData<number>,
  previousWidget: AbstractTemplateInsightWidgetData<number>,
): CompareInfo => {
  const compareInfo = {
    noOfProjects: {
      value: previousWidget.noOfProjects,
      isGood: currentWidget.noOfProjects >= previousWidget.noOfProjects,
      hasIncreased:
        round(currentWidget.noOfProjects) - round(previousWidget.noOfProjects),
    },
    avgDuration: {
      value: previousWidget.avgDuration,
      isGood: currentWidget.avgDuration <= previousWidget.avgDuration,
      hasIncreased:
        round(currentWidget.avgDuration) - round(previousWidget.avgDuration),
    },
    percentageStartedOnTime: {
      value: previousWidget.percentageStartedOnTime,
      isGood:
        currentWidget.percentageStartedOnTime >=
        previousWidget.percentageStartedOnTime,
      hasIncreased:
        round(currentWidget.percentageStartedOnTime) -
        round(previousWidget.percentageStartedOnTime),
    },
    averageStartingDelay: {
      value: previousWidget.averageStartingDelay,
      isGood:
        currentWidget.averageStartingDelay <=
        previousWidget.averageStartingDelay,
      hasIncreased:
        round(currentWidget.averageStartingDelay) -
        round(previousWidget.averageStartingDelay),
    },
    percentageCompletedOnTime: {
      value: previousWidget.percentageCompletedOnTime,
      isGood:
        currentWidget.percentageCompletedOnTime >=
        previousWidget.percentageCompletedOnTime,
      hasIncreased:
        round(currentWidget.percentageCompletedOnTime) -
        round(previousWidget.percentageCompletedOnTime),
    },
    averageCompletionDelay: {
      value: previousWidget.averageCompletionDelay,
      isGood:
        currentWidget.averageCompletionDelay <=
        previousWidget.averageCompletionDelay,
      hasIncreased:
        round(currentWidget.averageCompletionDelay) ===
        round(previousWidget.averageCompletionDelay)
          ? 0
          : currentWidget.averageCompletionDelay >
            previousWidget.averageCompletionDelay
          ? 1
          : -1,
    },
  };

  return compareInfo;
};

export const useProjectWidgetInTemplateInsight = ({
  currentProjectReport: projectReport,
  range,
  filter,
}: {
  currentProjectReport: ProjectsReport;
  range: DateRange;
  filter: Filter;
}): WidgetInfo => {
  const previousRange = useMemo(() => getPreviousRange(range), [range]);
  const isInitialized = !!filter.criteria.length;
  const previousProjectReport = useProjectsReport(
    previousRange,
    filter,
    isInitialized,
  );

  return useMemo(() => {
    const currentWidgetInfo = getProjectWidgetInTemplateInsight(projectReport);
    const previousWidgetInfo = getProjectWidgetInTemplateInsight(
      previousProjectReport,
    );

    if (
      currentWidgetInfo[0] === 'loading' ||
      previousWidgetInfo[0] === 'loading'
    ) {
      const getLoadingWidget = (loadingData) => ({
        noOfProjects: loadingData,
        avgDuration: loadingData,
        percentageStartedOnTime: loadingData,
        averageStartingDelay: loadingData,
        percentageCompletedOnTime: loadingData,
        averageCompletionDelay: loadingData,
      });

      return {
        currentWidgetInfo: getLoadingWidget(WidgetState.loading()),
      };
    }

    const compareInfo = getCompareInfo(
      currentWidgetInfo[1],
      previousWidgetInfo[1],
    );

    return {
      currentWidgetInfo: convertToWidgetData(currentWidgetInfo[1]),
      compareInfo,
    };
  }, [previousProjectReport, projectReport]);
};
